import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import {
    ContractSearchDto,
    ParcelSearchItem,
    SearchControllerService,
    SearchResultDto,
    SubjectContactResponseDto
} from '../../../api/backend'
import { AuthContextStorageService } from '../../../core/auth/auth-context-storage.service'
import { SelectedDepartmentService } from '../../../core/services/selected-department.service'

// Agregované vyhledávání drží výsledky jako plné DTO landing-page-search
// (subjekt / smlouva / parcela), aby je paleta vykreslila úplně stejně jako
// komponenta landing-page-search (rodné číslo, IČO, adresa, vlastníci/pachtýři,
// výměra) přes tytéž pipes a komponenty.
export interface EntitySearchResult {
    subjects: Array<SubjectContactResponseDto>;
    contracts: Array<ContractSearchDto>;
    parcels: Array<ParcelSearchItem>;
    // Celkové počty odpovídajících záznamů bez ohledu na limit (DEV-1746). Paleta z nich pozná,
    // že výsledků je víc, než kolik jich zobrazuje, a nabídne rozbalení nebo proklik do seznamu.
    subjectsTotal: number;
    contractsTotal: number;
    parcelsTotal: number;
    // Podle čeho backend hledal smlouvy — rozhoduje, které kritérium předvyplnit při prokliku
    // do seznamu smluv. Backend se větví sám, FE tu podmínku záměrně neduplikuje (DEV-1746).
    contractsMatchedByNumber: boolean;
}

// Volá generovaný klient `SearchControllerService.searchContractOrParcelOrSubject`
// (endpoint `{projectKey}/search`), který grupuje výsledky podle typu entity a
// vrací stejná DTO jako endpointy landing-page-search. Pokud má uživatel zapnuté
// středisko, posílá se `departmentId` jako u landing-page-search.
@Injectable({ providedIn: 'root' })
export class EntitySearchService {

    /** Počet položek na kategorii ve sbalené sekci palety. */
    public static readonly COLLAPSED_LIMIT = 5
    /** Počet položek na kategorii po rozbalení sekce. Nad tímto počtem paleta posílá do seznamu entity. */
    public static readonly EXPANDED_LIMIT = 10

    private static readonly MIN_QUERY_LENGTH = 2

    constructor(
        private readonly searchControllerService: SearchControllerService,
        private readonly authContext: AuthContextStorageService,
        private readonly selectedDepartmentService: SelectedDepartmentService
    ) {}

    private static emptyResult(): EntitySearchResult {
        return {
            subjects: [], contracts: [], parcels: [],
            subjectsTotal: 0, contractsTotal: 0, parcelsTotal: 0,
            contractsMatchedByNumber: false
        }
    }

    /**
     * @param limit počet položek na kategorii; `EXPANDED_LIMIT` při rozbalení sekce. Backend si limit
     *              sám stropuje, takže vyšší hodnota nic nezískává.
     */
    public search(query: string, limit: number = EntitySearchService.COLLAPSED_LIMIT): Observable<EntitySearchResult> {
        const trimmed = (query ?? '').trim()
        if (trimmed.length < EntitySearchService.MIN_QUERY_LENGTH) {
            return of(EntitySearchService.emptyResult())
        }

        const projectKey = this.authContext.getProjectKey()
        if (!projectKey) {
            return of(EntitySearchService.emptyResult())
        }

        // Pokud má uživatel vybrané středisko, omezí se vyhledávání jen na něj
        // (stejně jako v landing-page-search). Bez střediska se hledá napříč všemi.
        const departmentId = this.selectedDepartmentService.getSelectedDepartmentId() ?? undefined

        return this.searchControllerService
            .searchContractOrParcelOrSubject(projectKey, trimmed, limit, departmentId)
            .pipe(
                map(response => this.mapResponse(response)),
                catchError(() => of(EntitySearchService.emptyResult()))
            )
    }

    private mapResponse(response: SearchResultDto): EntitySearchResult {
        return {
            subjects: response?.subjects ?? [],
            contracts: response?.contracts ?? [],
            parcels: response?.parcels ?? [],
            // Proti starším backendům (bez DEV-1746) přijdou počty jako undefined → 0. Paleta pak
            // vyhodnotí, že není co rozbalovat, a chová se přesně jako dřív. Žádný fallback netřeba.
            subjectsTotal: response?.subjectsTotal ?? 0,
            contractsTotal: response?.contractsTotal ?? 0,
            parcelsTotal: response?.parcelsTotal ?? 0,
            contractsMatchedByNumber: response?.contractsMatchedByNumber ?? false
        }
    }
}
